import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import SocialPostTemplate, { NewsTemplate } from '../../components/mp/SocialPostTemplates';
import { exercisesApi, postsApi, platformsApi } from '../../lib/mpApi';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { copyToClipboard } from '../../lib/clipboard';
import { toast } from 'sonner';
import {
  ArrowLeft, Eye, Edit, Trash2, Send, Clock, Globe,
  Search, Plus, Radio, ExternalLink, RefreshCw, Copy,
  Monitor, Newspaper, BarChart3, CalendarClock, CheckCircle,
  ChevronDown, ChevronUp, Filter, MoreVertical, AlertCircle,
  FileText, GripVertical, Upload, Download,
} from 'lucide-react';
import SocialPostFormDialog from '../../components/mp/SocialPostFormDialog';
import NewsArticleFormDialog from '../../components/mp/NewsArticleFormDialog';
import NewsArticleDetailDialog from '../../components/mp/NewsArticleDetailDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NEWS_PLATFORMS = ['cnn', 'bbc', 'bbc_news', 'reuters', 'al_jazeera', 'fox_news', 'sky_news', 'the_guardian', 'nyt', 'washington_post', 'news'];
const isNewsPlatform = (name) => NEWS_PLATFORMS.some(k => (name || '').toLowerCase().includes(k));

const NEWS_COLORS = {
  cnn: '#CC0000', bbc: '#BB1919', bbc_news: '#BB1919', reuters: '#FF8000',
  al_jazeera: '#D2691E', fox_news: '#003366', sky_news: '#9B0000',
  the_guardian: '#005689', nyt: '#1A1A1A', washington_post: '#333',
};

const MPExerciseDashboard = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const isObserver = user?.roles?.includes('observer') && !user?.roles?.includes('super_admin') && !user?.roles?.includes('simcel');
  const [exercise, setExercise] = useState(null);
  const [posts, setPosts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [activeView, setActiveView] = useState('posts'); // posts | preview
  const [editPost, setEditPost] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  // V14.8 — type chooser → rich modal (matches the standalone Social/News pages)
  const [createType, setCreateType] = useState(null); // 'social' | 'news' | null
  const [editType, setEditType] = useState(null);     // 'social' | 'news' | null
  const [createForm, setCreateForm] = useState({ title: '', content: '', platform: '', status: 'draft' });
  const [creating, setCreating] = useState(false);
  const [detailPost, setDetailPost] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [importing, setImporting] = useState(false);
  const bulkFileRef = useRef(null);
  const refreshRef = useRef(null);
  const skipLoadRef = useRef(false);

  const loadData = useCallback(async () => {
    if (skipLoadRef.current) return;
    try {
      const [ex, postsList, plats] = await Promise.all([
        exercisesApi.getById(exerciseId),
        postsApi.getAll({ exercise_id: exerciseId }),
        platformsApi.getAll(),
      ]);
      if (skipLoadRef.current) return;
      setExercise(ex);
      setPosts(Array.isArray(postsList) ? postsList : postsList.posts || []);
      setPlatforms(plats);
    } catch (err) {
      toast.error('Failed to load exercise data');
    } finally {
      setLoading(false);
    }
  }, [exerciseId]);

  const reorderModeRef = useRef(false);
  useEffect(() => { reorderModeRef.current = reorderMode; }, [reorderMode]);

  useEffect(() => {
    loadData();
    refreshRef.current = setInterval(() => {
      if (!reorderModeRef.current && !skipLoadRef.current) loadData();
    }, 10000);
    return () => clearInterval(refreshRef.current);
  }, [loadData]);

  const filtered = posts.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (platformFilter !== 'all' && p.platform !== platformFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (p.title || '').toLowerCase().includes(q) ||
             (p.content || '').toLowerCase().includes(q) ||
             (p.platform || '').toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || new Date(b.timestamp) - new Date(a.timestamp));

  const handlePublish = async (postId) => {
    setPublishing(postId);
    try {
      await postsApi.publish(postId);
      toast.success('Post published');
      loadData();
    } catch (err) { toast.error('Failed to publish'); }
    finally { setPublishing(null); }
  };

  const handleUnpublish = async (postId) => {
    if (!window.confirm('Unpublish this post? It will return to draft and disappear from Live Display.')) return;
    setPublishing(postId);
    try {
      await postsApi.unpublish(postId);
      toast.success('Post unpublished');
      loadData();
    } catch (err) { toast.error('Failed to unpublish'); }
    finally { setPublishing(null); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await postsApi.delete(postId);
      toast.success('Post deleted');
      loadData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const openEdit = (post) => {
    // V14.8 — open the matching rich dialog (Social or News) instead of the
    // small generic edit form so the experience matches the standalone pages.
    setEditPost(post);
    setEditType(post.post_type === 'news' || platforms.find(p => p.name === post.platform)?.category === 'news' || isNewsPlatform(post.platform) ? 'news' : 'social');
    setEditForm({
      title: post.title || '',
      content: post.content || '',
      platform: post.platform || '',
      status: post.status || 'draft',
    });
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await postsApi.update(editPost.post_id, editForm);
      toast.success('Post updated');
      setEditPost(null);
      loadData();
    } catch (err) { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!createForm.content.trim()) { toast.error('Content is required'); return; }
    if (!createForm.platform) { toast.error('Please select a platform'); return; }
    setCreating(true);
    try {
      await postsApi.create({
        ...createForm,
        exercise_id: exerciseId,
        post_type: isNewsPlatform(createForm.platform) ? 'news' : 'social_media',
      });
      toast.success('Post created');
      setShowCreate(false);
      setCreateForm({ title: '', content: '', platform: '', status: 'draft' });
      loadData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail[0]?.msg : 'Failed to create post';
      toast.error(msg);
    }
    finally { setCreating(false); }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const result = await postsApi.bulkImport(file, exerciseId);
      const msg = `${result.imported} post(s) imported as draft`;
      if (result.skipped > 0) {
        toast.warning(`${msg}. ${result.skipped} row(s) skipped.`);
        result.errors?.forEach(err => toast.error(err, { duration: 6000 }));
      } else {
        toast.success(msg);
      }
      loadData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Bulk import failed');
    } finally {
      setImporting(false);
      if (bulkFileRef.current) bulkFileRef.current.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await postsApi.downloadTemplate('xlsx');
    } catch { toast.error('Failed to download template'); }
  };

  const statusCounts = {
    all: posts.length,
    published: posts.filter(p => p.status === 'published').length,
    scheduled: posts.filter(p => p.status === 'scheduled').length,
    draft: posts.filter(p => p.status === 'draft').length,
  };

  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (targetIdx) => {
    if (dragIndex === null || dragIndex === targetIdx) { setDragIndex(null); return; }
    const reordered = [...filtered];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIdx, 0, moved);
    const orderMap = {};
    reordered.forEach((p, i) => { orderMap[p.post_id] = i; });
    const order = reordered.map((p, i) => ({ post_id: p.post_id, sort_order: i }));
    skipLoadRef.current = true;
    setPosts(prev => prev.map(p =>
      orderMap[p.post_id] !== undefined ? { ...p, sort_order: orderMap[p.post_id] } : p
    ));
    setDragIndex(null);
    try {
      await postsApi.reorder(order);
      toast.success('Order saved');
      skipLoadRef.current = false;
      const postsList = await postsApi.getAll({ exercise_id: exerciseId });
      setPosts(Array.isArray(postsList) ? postsList : postsList.posts || []);
    } catch {
      skipLoadRef.current = false;
      toast.error('Failed to save order');
      loadData();
    }
  };

  const handleSortOrderChange = async (postId, newOrder) => {
    const num = parseInt(newOrder);
    if (isNaN(num) || num < 1) return;
    skipLoadRef.current = true;
    setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, sort_order: num } : p));
    try {
      await postsApi.update(postId, { sort_order: num });
      skipLoadRef.current = false;
      const postsList = await postsApi.getAll({ exercise_id: exerciseId });
      setPosts(Array.isArray(postsList) ? postsList : postsList.posts || []);
    } catch {
      skipLoadRef.current = false;
      toast.error('Failed to update order');
      loadData();
    }
  };

  const uniquePlatforms = [...new Set(posts.map(p => p.platform).filter(Boolean))];

  // Filter platforms to only show exercise-selected platforms, or used platforms as fallback
  const exercisePlatforms = (() => {
    const selected = exercise?.selected_platforms || [];
    if (selected.length) {
      return platforms.filter(p =>
        selected.some(sp => sp.toLowerCase() === (p.name || '').toLowerCase() || sp.toLowerCase() === (p.display_name || '').toLowerCase())
      );
    }
    // Fallback: show platforms already used in this exercise's posts (fuzzy match)
    if (uniquePlatforms.length) {
      return platforms.filter(p => {
        const pn = (p.name || '').toLowerCase();
        return uniquePlatforms.some(up => {
          const upl = up.toLowerCase();
          return upl === pn || upl.includes(pn) || pn.includes(upl);
        });
      });
    }
    return platforms; // Last resort: show all
  })();

  // Group for preview
  const publishedPosts = posts.filter(p => p.status === 'published');
  const platformGroups = {};
  publishedPosts.forEach(p => {
    const key = p.platform || 'unknown';
    if (!platformGroups[key]) platformGroups[key] = [];
    platformGroups[key].push(p);
  });

  if (loading) {
    return (
      <MPDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </MPDashboardLayout>
    );
  }

  if (!exercise) {
    return (
      <MPDashboardLayout>
        <div className="text-center py-16">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="text-muted-foreground">Exercise not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/mp/exercises')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Exercises
          </Button>
        </div>
      </MPDashboardLayout>
    );
  }

  return (
    <MPDashboardLayout>
      <div className="space-y-4" data-testid="mp-exercise-dashboard">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/mp/exercises')} data-testid="mp-exdash-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Exercises
          </Button>
          {exercise.logo_url && (
            <img src={exercise.logo_url.startsWith('/') ? `${BACKEND_URL}${exercise.logo_url}` : exercise.logo_url}
              alt="" className="w-10 h-10 rounded-lg object-contain border border-border bg-white flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate">{exercise.name}</h1>
            {exercise.description && <p className="text-xs text-muted-foreground truncate">{exercise.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Badge className={exercise.status === 'enabled' ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' : 'bg-muted text-muted-foreground'}>
              {exercise.status === 'enabled' ? 'Live' : exercise.status}
            </Badge>
            {exercise.guest_token && (
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                onClick={() => { copyToClipboard(`${BACKEND_URL}/guest/${exercise.guest_token}`).then(() => toast.success('Link copied')); }}
                data-testid="mp-exdash-copy-guest">
                <Copy className="w-3 h-3" /> Guest Link
              </Button>
            )}
            {/* V14.11.3 — Live Display uses JWT auth, not the guest token, so it
                works for every MP exercise regardless of guest_access status. */}
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
              onClick={() => window.open(`/mp/public-display?exercise=${exerciseId}`, '_blank')}
              data-testid="mp-exdash-live-display">
              <Monitor className="w-3 h-3" /> Live Display
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[
            { label: 'Total Posts', value: posts.length, color: 'text-foreground' },
            { label: 'Published', value: statusCounts.published, color: 'text-emerald-500' },
            { label: 'Scheduled', value: statusCounts.scheduled, color: 'text-blue-500' },
            { label: 'Drafts', value: statusCounts.draft, color: 'text-muted-foreground' },
            { label: 'Platforms', value: uniquePlatforms.length, color: 'text-purple-500' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-lg px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* View Toggle + Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-muted rounded-lg p-0.5">
            <button onClick={() => setActiveView('posts')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${activeView === 'posts' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
              data-testid="mp-exdash-tab-posts">
              <Newspaper className="w-3 h-3 inline mr-1" /> Posts
            </button>
            <button onClick={() => setActiveView('preview')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${activeView === 'preview' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
              data-testid="mp-exdash-tab-preview">
              <Monitor className="w-3 h-3 inline mr-1" /> Live Preview
            </button>
          </div>
          <div className="flex-1" />
          {!isObserver && (<>
          <input ref={bulkFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkImport} className="hidden" />
          <Button size="sm" variant="outline" onClick={handleDownloadTemplate} className="h-8 text-xs gap-1"
            data-testid="mp-exdash-download-template">
            <Download className="w-3.5 h-3.5" /> Template
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulkFileRef.current?.click()} disabled={importing} className="h-8 text-xs gap-1"
            data-testid="mp-exdash-bulk-import">
            {importing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Bulk Import
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs"
            data-testid="mp-exdash-create-post">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Post
          </Button>
          </>)}
        </div>

        {/* Posts View */}
        {activeView === 'posts' && (
          <div className="space-y-3">
            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Search posts..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-8 h-8 text-xs bg-muted border-input" data-testid="mp-exdash-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({statusCounts.all})</SelectItem>
                  <SelectItem value="published">Published ({statusCounts.published})</SelectItem>
                  <SelectItem value="scheduled">Scheduled ({statusCounts.scheduled})</SelectItem>
                  <SelectItem value="draft">Draft ({statusCounts.draft})</SelectItem>
                </SelectContent>
              </Select>
              <Select value={platformFilter} onValueChange={setPlatformFilter}>
                <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {uniquePlatforms.map(p => {
                    const plat = platforms.find(x => x.name === p);
                    return <SelectItem key={p} value={p}>{plat?.display_name || p}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Reorder Toggle */}
            {!isObserver && (
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
              onClick={() => setReorderMode(r => !r)}>
              <GripVertical className="w-3 h-3" />
              {reorderMode ? 'Done Reordering' : 'Reorder Posts'}
            </Button>
            )}

            {/* Post List */}
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Newspaper className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No posts found</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={() => setShowCreate(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Create First Post
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map((post, idx) => {
                  const plat = platforms.find(p => p.name === post.platform);
                  const isNews = post.post_type === 'news' || plat?.category === 'news' || isNewsPlatform(post.platform);
                  const statusColor = {
                    published: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
                    scheduled: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
                    draft: 'bg-muted text-muted-foreground border-border',
                  }[post.status] || 'bg-muted text-muted-foreground';

                  return (
                    <Card key={post.post_id} className="border-border hover:border-primary/20 transition-colors"
                      data-testid={`mp-exdash-post-${post.post_id}`}
                      draggable={reorderMode}
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(idx)}
                      style={reorderMode ? { cursor: 'grab' } : {}}>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-3">
                          {/* Order number + drag handle */}
                          <div className="flex flex-col items-center gap-1 flex-shrink-0 pt-0.5" onClick={e => e.stopPropagation()}>
                            {reorderMode && <GripVertical className="w-4 h-4 text-muted-foreground" />}
                            {!isObserver ? (
                              <input
                                type="number"
                                min="1"
                                value={post.sort_order ?? ''}
                                onChange={e => setPosts(prev => prev.map(p => p.post_id === post.post_id ? { ...p, sort_order: parseInt(e.target.value) || '' } : p))}
                                onBlur={e => handleSortOrderChange(post.post_id, e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                                className="w-8 h-6 text-[11px] font-mono text-center bg-muted border border-border rounded focus:ring-1 focus:ring-primary focus:border-primary text-foreground"
                                title="Change order number"
                                data-testid={`mp-sort-order-${post.post_id}`}
                              />
                            ) : (
                              <span className="w-8 h-6 text-[11px] font-mono text-center text-muted-foreground flex items-center justify-center">{post.sort_order ?? '-'}</span>
                            )}
                          </div>
                          {/* Platform icon */}
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ backgroundColor: plat?.color || NEWS_COLORS[post.platform?.toLowerCase()] || '#666' }}>
                            {(plat?.display_name || post.platform || '?').charAt(0).toUpperCase()}
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              {post.title && <span className="font-medium text-sm truncate">{post.title}</span>}
                              <Badge className={`text-[10px] ${statusColor}`}>{post.status}</Badge>
                              <span className="text-[10px] text-muted-foreground">{plat?.display_name || post.platform}</span>
                              {isNews && <Badge variant="outline" className="text-[10px]">News</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2">{post.content}</p>
                            <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                              {post.author?.name && <span>{post.author.name}</span>}
                              {post.published_at && <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> {new Date(post.published_at).toLocaleString()}</span>}
                              {post.scheduled_time && !post.published_at && <span className="flex items-center gap-0.5"><CalendarClock className="w-2.5 h-2.5" /> Scheduled: {new Date(post.scheduled_time).toLocaleString()}</span>}
                            </div>
                          </div>
                          {/* Actions */}
                          {!isObserver && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {post.status === 'draft' && (
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                                onClick={() => handlePublish(post.post_id)} disabled={publishing === post.post_id}
                                data-testid={`mp-exdash-publish-${post.post_id}`}>
                                {publishing === post.post_id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Publish
                              </Button>
                            )}
                            {post.status === 'scheduled' && (
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                                onClick={() => handlePublish(post.post_id)} disabled={publishing === post.post_id}>
                                {publishing === post.post_id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Publish Now
                              </Button>
                            )}
                            {post.status === 'published' && (
                              <Button variant="outline" size="sm" className="h-7 text-xs"
                                onClick={() => handleUnpublish(post.post_id)} disabled={publishing === post.post_id}
                                data-testid={`mp-exdash-unpublish-${post.post_id}`}>
                                Unpublish
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => openEdit(post)} data-testid={`mp-exdash-edit-${post.post_id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500"
                              onClick={() => handleDelete(post.post_id)} data-testid={`mp-exdash-delete-${post.post_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Preview View */}
        {activeView === 'preview' && (
          <Card data-testid="mp-exdash-preview">
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <Monitor className="w-3.5 h-3.5 text-red-500" />
                  Live Preview
                  <Badge variant="secondary" className="text-[10px] ml-1">{publishedPosts.length} published</Badge>
                </CardTitle>
                {exercise.guest_token && (
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                    onClick={() => window.open(`/mp/public-display?exercise=${exerciseId}`, '_blank')}>
                    <ExternalLink className="w-3 h-3" /> Open Full Display
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              {publishedPosts.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  <Radio className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No published posts to preview</p>
                  <p className="text-xs mt-1">Publish posts from the Posts tab to see them here</p>
                </div>
              ) : (
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {Object.entries(platformGroups).map(([platName, platPosts]) => {
                    const plat = platforms.find(p => p.name === platName);
                    // V14.13.11.3 — Category-aware news detection.
                    // If the platform's `category` is `news` OR the post itself
                    // was authored as `post_type=news`, treat as news → open
                    // the detail dialog on click and show a Read More affordance.
                    // Fixes: custom "news"-category platforms not being clickable
                    // in admin Live Preview (worked in guest view only).
                    const platIsNews = plat?.category === 'news' || isNewsPlatform(platName);
                    const color = plat?.color || NEWS_COLORS[platName.toLowerCase()] || '#666';
                    return (
                      <div key={platName} className="flex-shrink-0 border-r last:border-r-0 border-border pr-4"
                        style={{ width: Object.keys(platformGroups).length === 1 ? '100%' : '340px', minWidth: '300px' }}>
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border sticky top-0 bg-card z-10">
                          <div className="w-5 h-5 rounded flex items-center justify-center text-white text-[9px] font-bold"
                            style={{ backgroundColor: color }}>
                            {(plat?.display_name || platName).charAt(0).toUpperCase()}
                          </div>
                          <span className="text-xs font-bold uppercase tracking-wide">{plat?.display_name || platName}</span>
                          <Badge variant="secondary" className="text-[10px] ml-auto">{platPosts.length}</Badge>
                        </div>
                        <ScrollArea className="h-[500px]">
                          <div className="space-y-3 pr-2">
                            {platPosts.map(post => {
                              const isNews = platIsNews || post.post_type === 'news';
                              return (
                                <div key={post.post_id} className="transition-all">
                                  {isNews
                                    ? <NewsTemplate post={post} platformColor={color} onOpen={() => setDetailPost(post)} />
                                    : <SocialPostTemplate post={post} platform={post.platform} onOpen={() => setDetailPost(post)} />}
                                </div>
                              );
                            })}
                          </div>
                        </ScrollArea>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* V14.8 — Type chooser → rich Create dialog. Replaces the old small
            generic form so MP creation looks identical everywhere. */}
        <Dialog open={showCreate && createType === null} onOpenChange={(o) => { if (!o) { setShowCreate(false); setCreateType(null); } }}>
          <DialogContent className="max-w-md" data-testid="mp-exdash-type-chooser">
            <DialogHeader>
              <DialogTitle className="text-base font-heading uppercase">Create New Post</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground mb-2">Choose the type of post you want to create:</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setCreateType('social')}
                className="group flex flex-col items-center gap-2 p-5 rounded-lg border border-input hover:border-red-500 hover:bg-red-500/10 transition-all"
                data-testid="mp-exdash-pick-social">
                <FileText className="w-8 h-8 text-muted-foreground group-hover:text-red-500" />
                <span className="text-sm font-medium">Social Post</span>
                <span className="text-[10px] text-muted-foreground text-center">Twitter / LinkedIn / Facebook · with author profile, engagement & live preview</span>
              </button>
              <button onClick={() => setCreateType('news')}
                className="group flex flex-col items-center gap-2 p-5 rounded-lg border border-input hover:border-red-500 hover:bg-red-500/10 transition-all"
                data-testid="mp-exdash-pick-news">
                <Newspaper className="w-8 h-8 text-muted-foreground group-hover:text-red-500" />
                <span className="text-sm font-medium">News Article</span>
                <span className="text-[10px] text-muted-foreground text-center">BBC / CNN / Reuters · with headline, block editor & byline</span>
              </button>
            </div>
            <DialogFooter className="border-t border-border pt-3 mt-2">
              <Button variant="outline" onClick={() => { setShowCreate(false); setCreateType(null); }}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rich Social Post create dialog (locked to current exercise) */}
        <SocialPostFormDialog
          open={showCreate && createType === 'social'}
          onOpenChange={(o) => { if (!o) { setShowCreate(false); setCreateType(null); } }}
          editingPost={null}
          exercises={exercise ? [exercise] : []}
          platforms={platforms}
          lockedExerciseId={exerciseId}
          onSaved={() => { setShowCreate(false); setCreateType(null); loadData(); }}
        />

        {/* Rich News Article create dialog (locked to current exercise) */}
        <NewsArticleFormDialog
          open={showCreate && createType === 'news'}
          onOpenChange={(o) => { if (!o) { setShowCreate(false); setCreateType(null); } }}
          editingPost={null}
          exercises={exercise ? [exercise] : []}
          platforms={platforms}
          lockedExerciseId={exerciseId}
          onSaved={() => { setShowCreate(false); setCreateType(null); loadData(); }}
        />

        {/* Rich Edit dialogs — pick by post type */}
        <SocialPostFormDialog
          open={!!editPost && editType === 'social'}
          onOpenChange={(o) => { if (!o) { setEditPost(null); setEditType(null); } }}
          editingPost={editType === 'social' ? editPost : null}
          exercises={exercise ? [exercise] : []}
          platforms={platforms}
          lockedExerciseId={exerciseId}
          onSaved={() => { setEditPost(null); setEditType(null); loadData(); }}
        />
        <NewsArticleFormDialog
          open={!!editPost && editType === 'news'}
          onOpenChange={(o) => { if (!o) { setEditPost(null); setEditType(null); } }}
          editingPost={editType === 'news' ? editPost : null}
          exercises={exercise ? [exercise] : []}
          platforms={platforms}
          lockedExerciseId={exerciseId}
          onSaved={() => { setEditPost(null); setEditType(null); loadData(); }}
        />

        {/* V14.9 — News article full-detail viewer */}
        <NewsArticleDetailDialog open={!!detailPost} onOpenChange={(o) => !o && setDetailPost(null)} post={detailPost} />
      </div>
    </MPDashboardLayout>
  );
};

export default MPExerciseDashboard;
